import type { NavigationItem, NavigationSection } from "@repo/types";
import type { NormalizedUserModule } from "@repo/api-client";

const moduleDefaultRoute: Record<string, string> = {
  frontdesk: "/frontdesk-availability"
};

const ensureLeadingSlash = (path: string) => (path.startsWith("/") ? path : `/${path}`);

export const resolveModuleHref = (module: NormalizedUserModule) => {
  const fallback = moduleDefaultRoute[module.module_code] ?? `/${module.module_code}`;
  const candidate =
    module.route_paths.find((route) => typeof route === "string" && route.length > 0) ?? fallback;
  return ensureLeadingSlash(candidate);
};

export const buildSectionsFromModules = (modules: NormalizedUserModule[]): NavigationSection[] => {
  const sectionAccumulator = new Map<
    string,
    { order: number; label: string; items: NavigationItem[] }
  >();

  modules.forEach((module) => {
    const sectionKey = module.category || "others";
    const sectionLabel = module.section_label || "其他模組";
    const entry = sectionAccumulator.get(sectionKey);

    const navItem: NavigationItem = {
      id: module.module_code,
      label: module.name,
      href: resolveModuleHref(module),
      description: module.description,
      icon: module.icon,
      meta: { sortOrder: module.sort_order ?? 99 }
    };

    if (!entry) {
      sectionAccumulator.set(sectionKey, {
        order: module.section_sort_order ?? module.sort_order ?? 99,
        label: sectionLabel,
        items: [navItem]
      });
      return;
    }

    if (!entry.items.some((item) => item.id === navItem.id)) {
      entry.items.push(navItem);
    }
  });

  return Array.from(sectionAccumulator.entries())
    .sort((a, b) => a[1].order - b[1].order)
    .map(([id, value]) => ({
      id,
      label: value.label,
      items: value.items.sort(
        (a, b) => ((a.meta?.sortOrder as number) ?? 99) - ((b.meta?.sortOrder as number) ?? 99)
      )
    }));
};
